import { useRef } from 'react';
/**
 * Set focus to element
 *
 * @see https://github.com/carbon-design-system/carbon/blob/3943d39b9291f2cad0ddb76570380c270d14cac4/packages/react/src/internal/focus/index.js#L8
 */
export function safeFocus(element) {
    if (element && element.focus && document.activeElement !== element) {
        element.focus();
    }
}
/**
 * Utility to reference focusable elements in the current scope
 */
export function useFocusScope(ref, returnFocus, filter) {
    const focusScope = useRef(null);
    if (focusScope.current === null) {
        focusScope.current = createFocusScope(ref, returnFocus, filter);
    }
    return focusScope.current;
}
function isTabbable(node) {
    if (!(node instanceof HTMLElement) ||
        ('disabled' in node && node.disabled) ||
        node.getAttribute('type') === 'hidden') {
        return false;
    }
    return node.tabIndex >= 0;
}
/**
 * Find the tabbable elements for a given container
 *
 * @see https://github.com/whatwg/html/issues/2071#issuecomment-263736022
 */
function createFocusWalker(container, filter) {
    return document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
        acceptNode(node) {
            if (filter) {
                return isTabbable(node) && filter(node)
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_SKIP;
            }
            return isTabbable(node)
                ? NodeFilter.FILTER_ACCEPT
                : NodeFilter.FILTER_SKIP;
        },
    });
}
function createFocusScope(ref, returnFocus, filter) {
    const getTreeWalker = () => {
        if (!ref.current)
            return document.createTreeWalker(document);
        return createFocusWalker(ref.current, filter);
    };
    const getFirstDescendant = (skipNoAutofocus = false) => {
        const walker = getTreeWalker();
        let node = walker.firstChild();
        while (node instanceof HTMLElement &&
            skipNoAutofocus &&
            node.dataset.noAutofocus) {
            node = walker.nextNode();
        }
        return node;
    };
    const getLastDescendant = () => getTreeWalker().lastChild();
    const getNextDescendant = (currentNode, reverse = false) => {
        if (!currentNode) {
            return null;
        }
        const walker = getTreeWalker();
        walker.currentNode = currentNode;
        let nextNode = reverse ? walker.previousNode() : walker.nextNode();
        if (!nextNode && !(returnFocus === null || returnFocus === void 0 ? void 0 : returnFocus.current)) {
            nextNode = reverse ? getLastDescendant() : getFirstDescendant();
        }
        return nextNode;
    };
    return {
        getRootNode() {
            return ref.current;
        },
        getTreeWalker,
        getFirstDescendant,
        getLastDescendant,
        getNextDescendant,
        focusNextDescendant(currentNode, reverse = false) {
            const nextDescendant = getNextDescendant(currentNode, reverse);
            if (nextDescendant instanceof HTMLElement) {
                safeFocus(nextDescendant);
            }
            else if (returnFocus === null || returnFocus === void 0 ? void 0 : returnFocus.current) {
                safeFocus(returnFocus.current);
            }
        },
        focusFirstDescendant() {
            const firstChild = getFirstDescendant();
            if (firstChild instanceof HTMLElement) {
                safeFocus(firstChild);
            }
            else {
                safeFocus(ref.current);
            }
        },
        focusLastDescendant() {
            const lastChild = getLastDescendant();
            if (lastChild instanceof HTMLElement) {
                safeFocus(lastChild);
            }
        },
        autofocus() {
            const walker = getTreeWalker();
            let node = walker.nextNode();
            while (node != null) {
                if (node instanceof HTMLElement && node.dataset.autofocus) {
                    break;
                }
                node = walker.nextNode();
            }
            node = node || getFirstDescendant(true);
            if (node instanceof HTMLElement) {
                safeFocus(node);
            }
        },
    };
}
//# sourceMappingURL=useFocusScope.js.map